import { get } from './apiClient';

/**
 * The Accounts Payable visibility reports (D49 phase 2, BUSINESS_RULES#payable-aging):
 * aging, due schedule and what-we-owe-per-supplier. Every figure is summed by the server in
 * MongoDB, per currency: this file only types the answers. Nothing here adds two currencies
 * together, and the columns, labels and due-day ranges come FROM the server, so no day maths
 * or bucket rule is repeated in the browser.
 */

// ── Shared ─────────────────────────────────────────────────────────────────────────────────

/**
 * A TTD-equivalent of the per-currency figures, marked "indicative": it converts with the same
 * rate the bill list summary uses. A currency with no rate is named in `unconvertedCurrencies`
 * and left out of the figures rather than counted 1:1.
 */
export interface IndicativeTotals<K extends string> {
  currency: string;
  totals: Record<K, number>;
  unconvertedCurrencies: string[];
}

// ── Aging ──────────────────────────────────────────────────────────────────────────────────

export type AgingBucketKey = 'not_yet_due' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90_plus';
export type AgingColumnKey = AgingBucketKey | 'no_due_date' | 'on_hold_disputed' | 'total';

/** One column, in the order to draw. `dueFrom` / `dueTo` are the due days it covers (null = open-ended). */
export interface AgingColumn {
  key: AgingColumnKey;
  label: string;
  dueFrom: string | null;
  dueTo: string | null;
}

export type AgingFigures = Record<AgingColumnKey, number> & { billCount: number };
export type AgingSupplierRow = AgingFigures & { supplierName: string };

export interface AgingCurrencyBlock {
  currency: string;
  suppliers: AgingSupplierRow[];
  totals: AgingFigures & { supplierCount: number };
}

export interface AgingReport {
  /** The day the age is measured from (today unless a later day was asked for). */
  asOf: string;
  buckets: AgingColumn[];
  currencies: AgingCurrencyBlock[];
  indicative: IndicativeTotals<AgingColumnKey>;
}

export interface AgingParams {
  /** `yyyy-mm-dd`, today or later. Blank = today. */
  asOf?: string;
  currency?: string;
  /** The supplier's stored name, exactly. */
  supplier?: string;
}

function query(entries: Array<[string, string | undefined]>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of entries) {
    // Not trimmed: a supplier is matched by its stored name exactly, spaces and all.
    if (value !== undefined && String(value).trim() !== '') qs.set(key, String(value));
  }
  const text = qs.toString();
  return text ? `?${text}` : '';
}

export async function getAgingReport(params: AgingParams = {}) {
  return get<{ success: boolean; data: AgingReport }>(
    `/accounts-payable/aging${query([
      ['asOf', params.asOf],
      ['currency', params.currency],
      ['supplier', params.supplier],
    ])}`
  );
}

// ── Due schedule ───────────────────────────────────────────────────────────────────────────

export type DueScheduleGroupBy = 'day' | 'week';

export interface DueSchedulePeriod {
  start: string;
  end: string;
  label: string;
  amount: number;
  count: number;
}

export interface DueScheduleAmount {
  amount: number;
  count: number;
}

export interface DueScheduleCurrencyBlock {
  currency: string;
  /** Open, not held, due before the range starts. `dueTo` is the last day it covers. */
  carriedOver: DueScheduleAmount & { dueTo: string };
  periods: DueSchedulePeriod[];
  noDueDate: DueScheduleAmount;
  onHoldDisputed: DueScheduleAmount;
  /** Open, not held, due after the range ends. `dueFrom` is the first day it covers. */
  afterRange: DueScheduleAmount & { dueFrom: string };
  totals: {
    carriedOver: number;
    scheduled: number;
    noDueDate: number;
    onHoldDisputed: number;
    afterRange: number;
    owed: number;
    billCount: number;
  };
}

export type DueScheduleTotalKey = 'carriedOver' | 'scheduled' | 'noDueDate' | 'onHoldDisputed' | 'afterRange' | 'owed';

export interface DueScheduleReport {
  from: string;
  to: string;
  groupBy: DueScheduleGroupBy;
  currencies: DueScheduleCurrencyBlock[];
  indicative: IndicativeTotals<DueScheduleTotalKey>;
}

export interface DueScheduleParams {
  from?: string;
  to?: string;
  groupBy?: DueScheduleGroupBy;
  currency?: string;
}

export async function getDueSchedule(params: DueScheduleParams = {}) {
  return get<{ success: boolean; data: DueScheduleReport }>(
    `/accounts-payable/due-schedule${query([
      ['from', params.from],
      ['to', params.to],
      ['groupBy', params.groupBy],
      ['currency', params.currency],
    ])}`
  );
}

// ── Supplier balances ──────────────────────────────────────────────────────────────────────

export type SupplierBalanceSort = 'owed' | 'netOwed' | 'overdue' | 'oldest' | 'name';

export interface SupplierBalanceRow {
  supplierName: string;
  /** Every open bill, on hold or disputed included. */
  owed: number;
  /** Past due, leaving out bills on hold or disputed. A bill due today is not overdue. */
  overdue: number;
  oldestOverdueDays: number | null;
  onHoldDisputed: number;
  openBillCount: number;
  /** The supplier's open vendor credit in this currency. Matched to the bills BY NAME. */
  openCredit: number;
  /** owed - openCredit. Negative when the credit is larger than what is owed. */
  netOwed: number;
}

export type SupplierBalanceTotalKey = 'owed' | 'overdue' | 'onHoldDisputed' | 'openCredit' | 'netOwed';

export interface SupplierBalanceCurrencyBlock {
  currency: string;
  suppliers: SupplierBalanceRow[];
  totals: Record<SupplierBalanceTotalKey, number> & { supplierCount: number; openBillCount: number };
}

export interface SupplierBalancesReport {
  asOf: string;
  /** The last due day that counts as overdue (yesterday), for a link into the bill list. */
  overdueThrough: string;
  sort: SupplierBalanceSort;
  currencies: SupplierBalanceCurrencyBlock[];
  indicative: IndicativeTotals<SupplierBalanceTotalKey>;
}

export interface SupplierBalancesParams {
  currency?: string;
  q?: string;
  sort?: SupplierBalanceSort;
}

export async function getSupplierBalances(params: SupplierBalancesParams = {}) {
  return get<{ success: boolean; data: SupplierBalancesReport }>(
    `/accounts-payable/supplier-balances${query([
      ['currency', params.currency],
      ['q', params.q],
      ['sort', params.sort],
    ])}`
  );
}
